import { useEffect, useState, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { createUseStyles } from "react-jss";
import dateMath from "@elastic/datemath";
import {
  EuiButton,
  EuiFilterButton,
  EuiFilterGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormErrorText,
  EuiIcon,
  EuiSpacer,
  EuiBadge,
} from "@elastic/eui";
import moment from "moment-timezone";
import {
  AggregationIntervals,
  ExportHiddenClass,
  MAX_SELECTION_DAYS,
  TIME_OF_THE_DAY_MAPPING,
  TIME_TO_DAY_MAPPING,
} from "Consts";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { updateFilterBlockHeight } from "reduxStore/globalFilter/globalFilter.actions";
import { formatDays, isNullOrUndefined, isSubjectAuthorizedForUser } from "utils";
import FDChartIntervalSelect from "../IntervalSelect/ChartIntervalSelect";
import Emitter from "services/UtilsService/Emitter";
import FDFilterDropdown from "../FDFilterDropdown/FDFilterDropdown";
import FDFilterDropdownPerformance from "../FDFilterDropdown/performance/FDFilterDropdownPerformance";
import FDViewsDropdown from "../FDFilterDropdown/views/FDViewsDropdown";
import { topFilterWrapper } from "components/TopFilter/TopFilterWrapper";
import VerticalRule from "components/VerticalRule";
import {
  renderFilterBadgeContent,
  sortFilters,
  filterUniqueIds,
  getEnabledViews,
} from "./TopFilterUtils";
import Popover from "components/Popover/Popover";
import Button from "components/Buttons/Button";
import { ShareButton } from "components/Buttons/Share";
import { Roles, Subjects } from "settings/permissions";
import FDTimeSeriesIntervalButton from "components/IntervalSelect/FDTimeSeriesIntervalSelectButton";
import SideFilter from "../FDFilterDropdown/commonFilters/SideFilter";
import WisdomDatePicker from "components/WisdomDatePicker/WisdomDatePicker";
import "styles/components/topFilter/top_filter.css";

const useStyles = createUseStyles({
  interval_button: {
    "& .euiFilterButton__wrapper": {
      blockSize: "34px!important",
    },
  },
  datePicker: {
    "& .euiFormControlLayout .euiFormControlLayout--group": {
      height: "33px",
      marginTop: "6px",
    },
  },
});

const TopFilter = ({
  Filters,
  MaxSelectionDays = MAX_SELECTION_DAYS,
  MinSelectionDays = 0,
  OnTimeChange,
  OnResetClick,
  OnPeriodChange, // period should be interval
  RightFiltersControls = null,
  LeftControls = null,
  RightControls = null,
  DatePickerLeftControls = null,
  SetupButton = {},
  ShowDatePicker = false,
  ShowIntervalPicker = false,
  ShowResetButton = true,
  UseGlobalState = true, //update redux or use local state with the events (OnResetClick etc)
  StartDate = null, //currently supported when UseGlobalState=false
  EndDate = null, //currently supported when UseGlobalState=false
  Period = null, //currently supported when UseGlobalState=false
  AnomaliesModal = null,
  onDateError = () => {},
  filterId = null,
  isPerformaceFilter = false,
  isFixed = false,
  seriesData = null,
  isAutoRefresh = false,
  isShareEnabled = false,
}: any) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [filterChips, setFilterChips] = useState<any[]>([]);
  const [isChipsExpanded, setChipsExpanded] = useState(true);
  const [dateRangeError, setDateRangeError] = useState("");
  const [intervalSelectorVisible, setIntervalSelectorVisible] = useState(false);
  const reservedField = "findings_statusID";

  const alerts_overview = ["/health/alerts_overview"];
  const isAlerts = alerts_overview?.includes(window.location.pathname);
  const _MAX_SELECTION_DAYS = isAlerts ? 731 : MaxSelectionDays;

  const {
    clientId,
    GlobalTopFilterObject,
    GlobalTopFilterObjectValues,
    SelectedPeriod,
    IsGlobalFilterMode,
    DatePickerParams,
    LocalDatePickerParams,
    PerformanceFilterObject,
    navIsOpen,
  } = useSelector(globalFilterSelector, shallowEqual);

  const isFilterOnly = !Filters?.map((i: any) => i.field).includes("tag");

  const topFilters = Filters.filter(
    (i: any) => i.field === "tag" || i.field === "flyout_tag" || i.field === "resourceName"
  );
  const restFilters = Filters.filter(
    (i: any) => i.field !== "tag" && i.field !== "flyout_tag" && i.field !== "resourceName"
  );
  const _filters = useMemo(() => [...topFilters, ...restFilters], [clientId]);

  const chipsSymbolsPerLine = filterChips
    .filter((_, index) => index <= 3)
    .map((i: any) => i?.filterName?.concat(i.name))
    .join(",")?.length;

  const chipsLimitPerSingleLine = chipsSymbolsPerLine <= 90 ? 4 : 3;
  const visibleChips = isChipsExpanded ? 21 : chipsLimitPerSingleLine;

  const currentDatePickerParams = IsGlobalFilterMode ? DatePickerParams : LocalDatePickerParams;

  const sideFilters = { ...GlobalTopFilterObject };
  delete sideFilters?.tag;
  delete sideFilters?.sqlStatementHash;

  const _filteredFields = _filters.map((i) => i.field);
  const filteredSideFilters = Object.fromEntries(
    Object.entries(sideFilters).filter(([field]) => _filteredFields.includes(field))
  );

  const selectedSideFiltersCount = Object.values(filteredSideFilters)
    ?.flat()
    ?.filter((i) => i !== null)?.length;

  const filteredAllFilters = Object.fromEntries(
    Object.entries(GlobalTopFilterObject).filter(([field]) => _filteredFields.includes(field))
  );

  const allSelectedSideFiltersCount = Object.values(filteredAllFilters)
    ?.flat()
    ?.filter((i) => i !== null)?.length;

  const performanceFilter =
    !isNullOrUndefined(PerformanceFilterObject) &&
    !isNullOrUndefined(PerformanceFilterObject[filterId])
      ? PerformanceFilterObject[filterId]
      : {};

  const storeFilter = isPerformaceFilter ? performanceFilter : GlobalTopFilterObject;
  const [period, setPeriod] = useState(Period || SelectedPeriod);
  //currently supported when UseGlobalState=false
  const [dateRange, setDateRange] = useState(
    StartDate && EndDate ? { end: EndDate, start: StartDate } : currentDatePickerParams
  );

  const canReadView = isSubjectAuthorizedForUser({
    subject: Subjects.User_Settable_Management,
    role: Roles.Read,
  });

  const updateGlobalState = (obj: any) => {
    if (UseGlobalState) {
      dispatch(obj);
    }
  };

  useEffect(() => {
    if (!isNullOrUndefined(StartDate)) {
      setDateRange((d: any) => ({ ...d, start: StartDate }));
    }
  }, [StartDate]);

  useEffect(() => {
    if (!isNullOrUndefined(EndDate)) {
      setDateRange((d: any) => ({ ...d, end: EndDate }));
    }
  }, [EndDate]);

  useEffect(() => {
    if (!isNullOrUndefined(Period)) {
      setPeriod(Period);
    }
  }, [Period]);

  useEffect(() => {
    if (isPerformaceFilter === true) return;
    if (storeFilter) {
      const selectedFilters: any[] = [];

      const filterFields = _filters.map((f) => f.field);

      Object.entries(storeFilter).forEach(([key, val]: any) => {
        const keyExists = filterFields.includes(key);
        if (keyExists && val) {
          if (val?.length > 0) {
            const uniq: any = new Set(val);
            const chips: any[] = [...uniq].map((i) => ({
              field: key,
              filterName: _filters.find((i) => i.field === key)?.name,
              name: key === "TimeOfDay" ? TIME_OF_THE_DAY_MAPPING[i] : `${i}`,
            }));

            selectedFilters.push(...chips);
          }
        }
      });

      setFilterChips(selectedFilters);
    }
  }, [_filters, GlobalTopFilterObject, PerformanceFilterObject]);

  useEffect(() => {
    const filterTarget: any = document.getElementById("filter_box");
    const filterTargetHeight = filterTarget?.offsetHeight || 0;
    dispatch(updateFilterBlockHeight(filterTargetHeight));
  }, [filterChips, isChipsExpanded, navIsOpen]);

  const removeFilterChip = (chip: any) => {
    if (
      chip?.field === reservedField &&
      GlobalTopFilterObject[reservedField]?.length === 1 &&
      !isPerformaceFilter
    ) {
      Emitter.emit(
        "displayInfo",
        "One status must always be selected. Please select another one before deleting this one."
      );
    } else {
      if (UseGlobalState) {
        const chipName = chip.field === "TimeOfDay" ? TIME_TO_DAY_MAPPING[chip?.name] : chip?.name;
        const chipIdx = GlobalTopFilterObject[chip.field]?.findIndex(
          (c: any) => c === chipName.trim()
        );
        const values = GlobalTopFilterObjectValues ? GlobalTopFilterObjectValues[chip.field] : [];

        dispatch({
          payload: {
            [chip.field]: values?.filter((i: any, idx: number) => idx !== chipIdx),
          },
          type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
        });

        dispatch({
          payload: {
            [chip.field]: GlobalTopFilterObject[chip.field].filter((i: any) => i !== chipName),
          },
          type: globalFilterTypes.UPDATE_TOP_FILTER,
        });
      }

      if (chip.field === "tag" || chip.field === "flyout_tag") {
        const _tags_in_state = GlobalTopFilterObject[chip.field].filter(
          (i: any) => i !== chip?.name
        );
        if (_tags_in_state?.length === 0) Emitter.emit("TAG_FILTERS_RESET", null);
      }
    }

    if (isPerformaceFilter) {
      dispatch({
        payload: {
          key: filterId,
          [filterId]: {
            [chip.field]: performanceFilter[chip.field].filter((i: any) => i !== chip.name),
          },
        },
        type: globalFilterTypes.UPDATE_PERFORMANCE_FILTER,
      });
    }
  };

  const resetClick = () => {
    if (UseGlobalState) {
      updateGlobalState({
        payload: null,
        type: globalFilterTypes.UPDATE_TOP_FILTER,
      });

      updateGlobalState({
        payload: null,
        type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
      });

      updateGlobalState({
        type: globalFilterTypes.SET_ACTIVE_FILTERS,
        payload: null,
      });
    }

    Emitter.emit("TAG_FILTERS_RESET", null);

    if (isPerformaceFilter) {
      dispatch({
        payload: {
          key: filterId,
          [filterId]: null,
        },
        type: globalFilterTypes.CLEAR_PERFORMANCE_FILTER,
      });

      if (OnResetClick) {
        OnResetClick();
      }
    }

    //in case the page works with local state and wants to reset it
    if (OnResetClick) {
      filterUniqueIds.forEach((filterUniqueId, index) => {
        const onResetEventKey = "FDFilterDropdown_onreset_" + filterUniqueId;
        //@ts-ignore
        Emitter.emit(onResetEventKey);
      });

      OnResetClick();
    }
    setFilterChips([]);
  };

  const isSelectedDateRangeValid = (start: any, end: any) => {
    //@ts-ignore
    const startDate: any = start?.includes("now") ? dateMath.parse(start) : moment(start);

    const allowedFrom = moment().subtract(_MAX_SELECTION_DAYS, "days");
    const allowedTo = moment().subtract(MinSelectionDays, "days").endOf("day");

    let isValid = true;

    const _end = moment(end);
    if (
      window.location.pathname?.includes("alerts_overview") ||
      window.location.pathname?.includes("alerts_details")
    ) {
      return true;
    }
    if (window.location.pathname === "/admin_console/monitoring/silencing_windows") {
      return true;
    }
    if (
      window.location.pathname === "/performance/query_statistics_overview" &&
      _end.diff(startDate, "hours", true) < 1
    ) {
      isValid = false;
      onDateError();
      setDateRangeError(
        `The minimum required time period is 1 hour. Please adjust your selection.`
      );
    }
    if (startDate < allowedFrom) {
      isValid = false;
      onDateError();
      setDateRangeError(`Date range must be less than ${formatDays(_MAX_SELECTION_DAYS)}`);
    } else if (startDate > allowedTo) {
      isValid = false;
      onDateError();
      setDateRangeError(`Date range must be greater than ${formatDays(MinSelectionDays)}`);
    }

    if (isValid) {
      setDateRangeError("");
    }

    return isValid;
  };

  useEffect(() => {
    isSelectedDateRangeValid(DatePickerParams?.start, DatePickerParams?.end);
  }, [DatePickerParams]);

  const onTimeChange = ({ start, end }: any) => {
    const isValid = isSelectedDateRangeValid(start, end);
    if (isValid) {
      setDateRangeError("");

      if (UseGlobalState) {
        const datePickerParamsKey = IsGlobalFilterMode
          ? "DatePickerParams"
          : "LocalDatePickerParams";
        updateGlobalState({
          // todo: action for date as this is confusing
          payload: {
            [datePickerParamsKey]: {
              end,
              start,
            },
          },
          type: globalFilterTypes.SET_CURRENT_FILTER,
        });
      } else {
        setDateRange({ end, start });
      }

      if (OnTimeChange) {
        OnTimeChange({
          end,
          start,
        });
      }
    }
  };

  //@ts-ignore
  const isSetupButtonAuthorizedForUser: any = SetupButton.RequiredPermissions
    ? //@ts-ignore
      isSubjectAuthorizedForUser(SetupButton.RequiredPermissions)
    : true;

  const selectedFilter: any = {};
  const filterFields = (Filters || []).map((f: any) => f.field);

  Object.entries(storeFilter).forEach(([key, val]) => {
    const keyExists = filterFields.includes(key);
    if (keyExists && val) {
      selectedFilter[key] = val;
    }
  });

  const isFilterEmpty =
    (!Filters || Filters?.length === 0) &&
    //@ts-ignore
    (!RightFiltersControls || RightFiltersControls?.length === 0) &&
    //@ts-ignore
    !SetupButton.OnClick &&
    !DatePickerLeftControls &&
    !ShowIntervalPicker &&
    !AnomaliesModal &&
    !ShowDatePicker &&
    !LeftControls &&
    !RightControls;

  if (isFilterEmpty) return <></>;

  const sortedFilters = sortFilters(Filters); // Sort the filters here
  const isViewsEnabled = getEnabledViews();

  const filterRow = (
    <>
      <EuiFlexGroup
        alignItems="center"
        className="xx"
        gutterSize={"m"}
        justifyContent="flexStart"
        responsive={false}
        wrap>
        <>
          <EuiFlexItem grow={false}>
            <EuiFlexGroup
              alignItems="center"
              gutterSize={isFilterOnly ? "none" : "s"}
              responsive={false}
              wrap>
              {LeftControls && (
                <EuiFlexItem style={{ marginRight: "16px" }} grow={false}>
                  {LeftControls}
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <div>
                  <>
                    {!isPerformaceFilter && isViewsEnabled && canReadView && (
                      <>
                        <FDViewsDropdown />
                        <VerticalRule />
                      </>
                    )}

                    {/* TAG AND VIEWS FILTERING */}
                    {!isPerformaceFilter &&
                      topFilters.map((filter: any, index: number) => {
                        // Use sortedFilters
                        return (
                          <span key={`${filter.name}_${index}`}>
                            <FDFilterDropdown
                              AllowExclude={filter.allowExclude}
                              FdType={filter.fdType}
                              Field={filter.field}
                              ForceSelectedIndex={filter.forceSelectedIndex}
                              ForceSelectedValue={filter.forceSelectedValue}
                              //@ts-ignore
                              ID={filterUniqueIds[index]}
                              IsItemVisibleFunc={filter.isItemVisibleFunc}
                              Items={filter.fdType ? null : filter.options}
                              MaxSelectedItems={filter.maxSelectedItems}
                              Name={filter?.selected || filter.name}
                              OnClick={filter.onclick}
                              OnMaxSelection={filter.onMaxSelection}
                              OptionsFunc={filter.optionsFunc}
                              SingleSelection={filter.singleSelection == 1}
                              UpdateGlobalFilter={UseGlobalState && filter.updateGlobalFilter}
                              key={filter.name} //patch/infra for resetting the dropdown filter
                              popoverButtonProps={filter.popoverButtonProps}
                              filterList={sortedFilters}
                              isViewsEnabled={isViewsEnabled}
                              tagView={filter?.tagView || null}
                            />
                          </span>
                        );
                      })}

                    {/* PERFORMANCE FILTERING */}
                    {isPerformaceFilter &&
                      sortedFilters.map((filter, index) => {
                        // Use sortedFilters
                        return (
                          <span key={`${filter.name}_${index}`}>
                            <FDFilterDropdownPerformance
                              AllowExclude={filter.allowExclude}
                              isDisabled={filter.disabled}
                              FdType={filter.fdType}
                              Field={filter.field}
                              ForceSelectedIndex={filter.forceSelectedIndex}
                              ForceSelectedValue={filter.forceSelectedValue}
                              //@ts-ignore
                              ID={filterId + filterUniqueIds[index]}
                              IsItemVisibleFunc={filter.isItemVisibleFunc}
                              Items={filter.fdType ? null : filter.options}
                              MaxSelectedItems={filter.maxSelectedItems}
                              Name={filter.name}
                              OnCreate={filter.oncreate}
                              OnClick={filter.onclick}
                              OnApply={filter.onapply}
                              OnEdit={filter.onedit}
                              OnMaxSelection={filter.onMaxSelection}
                              SingleSelection={filter.singleSelection == 1}
                              //  Size={Size}
                              UpdateGlobalFilter={false}
                              key={filter.name} //patch/infra for resetting the dropdown filter
                              popoverButtonProps={filter.popoverButtonProps}
                              perfmonFilterId={filterId}
                              isPerformaceFilter={isPerformaceFilter}
                              actionButtonEnabled={filter?.actionButtonEnabled}
                              isHoverEnabled={filter?.isHoverEnabled}
                            />
                            {filter.fdType === "resource_tags_v2" &&
                              sortedFilters.length > 1 &&
                              index === 0 && <VerticalRule />}
                          </span>
                        );
                      })}
                  </>
                </div>
              </EuiFlexItem>

              {/* COMMON FILTERING */}
              {!isPerformaceFilter && restFilters?.length > 0 && (
                <EuiFlexItem grow={false} style={{ minWidth: 100 }}>
                  <SideFilter Filters={restFilters} selectedFilters={selectedSideFiltersCount} />
                </EuiFlexItem>
              )}

              {Array.isArray(RightFiltersControls) &&
                //@ts-ignore
                RightFiltersControls.map((rb, index) => {
                  return (
                    <EuiFlexItem
                      grow={false}
                      key={`RightFiltersControls_${index}`}
                      style={{ marginLeft: 10 }}>
                      {rb}
                    </EuiFlexItem>
                  );
                })}
            </EuiFlexGroup>
          </EuiFlexItem>

          {Array.isArray(Filters) && Filters.length > 0 && ShowResetButton && (
            <EuiFlexItem grow={false}>
              <Button
                type="secondary"
                disabled={allSelectedSideFiltersCount === 0}
                onClick={() => (allSelectedSideFiltersCount > 0 ? resetClick() : null)}
                text="Reset"
              />
            </EuiFlexItem>
          )}

          {/* rest is sticked to the right */}

          <EuiFlexItem grow></EuiFlexItem>
          {/* @ts-ignore */}
          {SetupButton.Text && SetupButton.OnClick && isSetupButtonAuthorizedForUser && (
            <EuiFlexItem grow={false}>
              {/* @ts-ignore */}
              <EuiButton fill fullWidth={false} onClick={(e: any) => SetupButton.OnClick(e)}>
                {/* @ts-ignore */}
                {SetupButton.Text}
              </EuiButton>
            </EuiFlexItem>
          )}

          {DatePickerLeftControls && (
            <EuiFlexItem grow={false}>{DatePickerLeftControls}</EuiFlexItem>
          )}

          {ShowIntervalPicker && (
            <EuiFlexItem grow={false} style={{ marginRight: "-24px" }}>
              <EuiFilterGroup style={{ boxShadow: "none", backgroundColor: "transparent" }}>
                {ShowIntervalPicker && (
                  <>
                    {seriesData && (
                      <div className={classes.interval_button}>
                        <FDTimeSeriesIntervalButton
                          end={dateRange.end}
                          interval={UseGlobalState ? null : period}
                          onChange={(interval: string) => {
                            setPeriod(interval);
                            if (OnPeriodChange) OnPeriodChange(interval);
                          }}
                          seriesData={seriesData}
                          start={dateRange.start}
                          useGlobalState={false}
                          disabled={isAutoRefresh}
                        />
                      </div>
                    )}
                    {!seriesData && (
                      <Popover
                        className={classes.interval_button}
                        button={
                          <EuiFilterButton
                            iconSide="left"
                            iconType={"recentlyViewedApp"}
                            style={{
                              background: "var(--card-background) !important",
                            }}
                            onClick={() => {
                              setIntervalSelectorVisible((v) => !v);
                            }}
                            title="Chart Interval">
                            {period === "Auto" && period + " (1h)"}
                            {period !== "Auto" && (period || AggregationIntervals[0].id)}
                          </EuiFilterButton>
                        }
                        onClose={() => {
                          setIntervalSelectorVisible(false);
                        }}
                        isOpen={intervalSelectorVisible}
                        children={
                          <FDChartIntervalSelect
                            end={UseGlobalState ? currentDatePickerParams.end : dateRange.end}
                            // @ts-ignore
                            onChange={(interval: string) => {
                              setPeriod(interval);
                              if (OnPeriodChange) OnPeriodChange(interval);
                              setIntervalSelectorVisible(false);
                            }}
                            selected={UseGlobalState ? null : period}
                            start={UseGlobalState ? currentDatePickerParams.start : dateRange.start}
                            useGlobalState={UseGlobalState}
                          />
                        }
                      />
                    )}
                  </>
                )}
              </EuiFilterGroup>
            </EuiFlexItem>
          )}

          {ShowDatePicker && (
            <EuiFlexItem data-testid="super-date-picker-id" grow={false}>
              <EuiFlexGroup>
                <EuiFlexItem grow={false}>
                  {dateRangeError && (
                    <EuiFormErrorText className="euiFormRow__text">
                      {dateRangeError}
                    </EuiFormErrorText>
                  )}
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <WisdomDatePicker
                    dateRange={dateRange}
                    isAutoRefresh={isAutoRefresh}
                    onApply={({ start, end }: any) => {
                      onTimeChange({ start, end });
                    }}
                    onCancel={() => {}}
                  />
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}

          {RightControls && <EuiFlexItem grow={false}>{RightControls}</EuiFlexItem>}
          {isShareEnabled && (
            <EuiFlexItem grow={false}>
              <ShareButton />
            </EuiFlexItem>
          )}
        </>
      </EuiFlexGroup>
      {filterChips.length > 0 && (
        <EuiFlexGroup>
          <EuiFlexGroup className="chips-group" wrap>
            {filterChips
              .filter((_, index) => index <= visibleChips - 1)
              .map((chip) => {
                return (
                  <EuiFlexItem grow={false}>
                    <EuiBadge
                      className="chips-badge"
                      color=""
                      iconOnClick={() => {
                        removeFilterChip(chip);
                      }}
                      iconOnClickAriaLabel="delete"
                      iconSide="right"
                      iconType="cross"
                      key={chip.name}>
                      {renderFilterBadgeContent(chip)}
                    </EuiBadge>
                  </EuiFlexItem>
                );
              })}
            {filterChips.length > visibleChips && (
              <EuiFlexItem className="chips-message" grow={false}>
                ... and {filterChips.length - visibleChips} additional filters.
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          {filterChips.length > 1 && (
            <EuiFlexGroup>
              <EuiFlexItem grow={false}>
                <div className="expand-button" onClick={() => setChipsExpanded(!isChipsExpanded)}>
                  <EuiIcon size="s" type={isChipsExpanded ? "arrowUp" : "arrowDown"} />
                </div>
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
        </EuiFlexGroup>
      )}
    </>
  );

  const filterContent = (
    <div id="filter_box" className={ExportHiddenClass}>
      {filterRow}
      <EuiSpacer size={"s"} />
    </div>
  );

  return <>{isFixed ? topFilterWrapper(filterContent) : filterContent}</>;
};

export default TopFilter;
